<template>
	<Modal id="wishlistModal" v-if="show" @hide="$emit('update:show', !show)" max-width="max-w-lg">
		<div v-if="!showItemAlreadyInWishlist" class="p-4 text-ihh-midnight-gray w-2xl">
			<div v-if="store?.user?.id">
				<span class="flex justify-center text-lg font-semibold mb-2">{{ creatingWishlist ? 'Create Wishlist' : 'Add to Wishlist' }}</span>
				<div v-if="!creatingWishlist" class="mb-4">
					<select v-model="selectedWishlist" class="block w-full p-2 border border-gray-300 rounded-md">
						<option disabled selected value="" class="text-center text-sm">Select a wishlist</option>
						<option v-for="wishlist in store.wishlists" :key="wishlist.id" :value="wishlist.id" class="text-center text-sm">{{ wishlist.name }}</option>
					</select>
				</div>
				<div v-if="creatingWishlist" class="mb-4">
					<input v-model="newWishlist.name" type="text" class="block w-full p-2 border border-gray-300 rounded-md" placeholder="New wishlist name" />
					<div class="flex mt-4">
						<input type="checkbox" v-model="newWishlist.isPublic" />
						<span class="ml-2">Make list shareable?</span>
					</div>
				</div>
				<button
					v-if="!creatingWishlist"
					:disabled="!selectedWishlist"
					@click="addToWishlist"
					class="w-full bg-ihh-dark-salmon font-ihh-bold text-white p-2 rounded-md hover:bg-ihh-salmon"
				>
					Add
				</button>
				<button
					v-if="creatingWishlist"
					@click="createWishlist"
					:disabled="!isValidName"
					class="w-full bg-ihh-dark-salmon font-ihh-bold text-white p-2 rounded-md hover:bg-ihh-salmon"
				>
					Create
				</button>
				<button @click="creatingWishlist = !creatingWishlist" class="w-full flex justify-center text-xs text-gray-400 mt-2 underline underline-offset-2">
					{{ creatingWishlist ? 'Select Existing Wishlist' : 'Create a New Wishlist' }}
				</button>
			</div>
			<div class="flex flex-col items-center justify-center h-64 text-center p-4" v-else>
				<p class="text-lg font-semibold text-gray-700">Please log in to manage your wishlists.</p>
				<router-link to="/login" class="bg-ihh-dark-salmon text-ihh-white rounded-lg px-14 py-2 mt-4 mb-2">Login</router-link>
				<button @click="$emit('update:show', false)" class="text-xs underline underline-offset-2 text-ihh-gray-500">Return to products</button>
			</div>
		</div>
		<div v-else class="w-full flex flex-col items-center justify-center h-64 text-center p-4">
			<div class="w-full flex items-center justify-center">
				<img src="/images/ob-heart-0129.svg" class="w-12 h-12" />
				<div class="text-3xl text-ihh-dark-salmon">Good News!</div>
			</div>
			<p class="text-base my-4 font-semibold text-ihh-midnight-gray">
				This item is already in your wishlist. Click OK to choose a different list or Exit to return to products.
			</p>
			<div class="flex gap-2">
				<button @click="handleOK" class="text-base text-white bg-ihh-dark-salmon rounded-lg px-14 py-2 mt-4 mb-2">OK</button>
				<button @click="handleExit" class="text-base text-ihh-midnight-gray border-ihh-dark-salmon border-2 rounded-lg px-14 py-2 mt-4 mb-2">Exit</button>
			</div>
		</div>
	</Modal>
</template>

<script>
import Swal from 'sweetalert2'
import Mango from '../../helpers/mango.js'

export default {
	name: 'WishlistModal',
	props: {
		show: {
			type: Boolean,
			default: false,
		},
		product: {
			type: Object,
			required: true,
		},
	},
	emits: ['update:show'],
	inject: ['store'],
	data() {
		return {
			creatingWishlist: false,
			selectedWishlist: '',
			newWishlist: {
				name: '',
				isPublic: false,
				bcCustomerId: this.store?.user?.bcId || null,
				items: [],
			},
			saving: false,
			showItemAlreadyInWishlist: false,
		}
	},
	methods: {
		async createWishlist() {
			this.saving = true

			try {
				let response = await Mango.wishlists.save(this.newWishlist, false, true)
				if (response.response?.id && !response?.warnings) {
					console.log(`Saving wishlist: ${response}`)
					this.store.wishlists.push(response.response)
					this.saving = false
					Swal.fire({
						icon: 'success',
						title: 'Wishlist Created',
						showConfirmButton: false,
						timer: 2000,
						toast: true,
						position: 'top-end',
					})

					this.selectedWishlist = response?.response?.id || null
					this.creatingWishlist = false
				} else {
					Swal.fire({ text: `Error saving list: ${response?.warnings}`, icon: 'error' })
				}
			} catch (error) {
				console.log(error)
			}
		},
		async addToWishlist() {
			if (!this.isUnique) {
				this.showItemAlreadyInWishlist = true
				return
			}

			console.log('Selected Wishlist: ', this.selectedWishlist)
			console.log('Current Product: ', this.product.name)
			let selectedWishlist = this.store.wishlists.find((w) => w.id === this.selectedWishlist)
			if (selectedWishlist) {
				console.log('Selected Wishlist: ', selectedWishlist.name)
				let response = await Mango.wishlists.save({
					id: this.selectedWishlist,
					$push: { items: { name: this.product.name, product: [this.product.id], productBcId: this.product.bcId } },
				})
				console.log('Response from Mango: ', response)
				selectedWishlist.items.push({
					name: this.product.name,
					product: [this.product.id],
					productBcId: this.product.bcId,
				})

				if (response?.id) {
					await this.syncWishlists()

					Swal.fire({
						icon: 'success',
						title: 'Added to Wishlist',
						showConfirmButton: false,
						timer: 2000,
						toast: true,
						position: 'center',
					})

					this.$emit('update:show', false)
					this.selectedWishlist = ''
				}
			}
		},
		isUnique() {
			const currentWishlist = this.store.wishlists.find((w) => w.id === this.selectedWishlist)
			if (currentWishlist) {
				if (!currentWishlist?.items?.length) {
					return true
				}
				let existingItem = currentWishlist.items.find((i) => i.productBcId === this.product.bcId)
				if (existingItem) {
					return false
				}
				return true
			}
			return false
		},
		async syncWishlists() {
			let response = await Mango.wishlists({ search: { author: this.store.user.id }, limit: 100 })
			if (response) {
				this.store.wishlists = response
			}
		},
		handleExit() {
			this.showItemAlreadyInWishlist = false
			this.selectedWishlist = ''
			this.$emit('update:show', false)
		},
		handleOK() {
			this.showItemAlreadyInWishlist = false
			this.selectedWishlist = ''
		},
	},
	computed: {
		isValidName() {
			return this.newWishlist?.name?.length > 0
		},
		isUnique() {
			const currentWishlist = this.store.wishlists.find((w) => w.id === this.selectedWishlist)
			if (currentWishlist) {
				if (!currentWishlist?.items?.length) {
					return true
				}
				let existingItem = currentWishlist.items.find((i) => i.productBcId === this.product.bcId)
				if (existingItem) {
					return false
				}
				return true
			}
			return true
		},
	},
	watch: {
		show: {
			handler(newVal) {
				if (newVal) {
					this.syncWishlists()
				}
			},
		},
	},
}
</script>
